import {
  AppBar, Avatar, Box, IconButton, Menu, MenuItem,
  Toolbar, Typography, Divider, useMediaQuery,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import MenuIcon        from "@mui/icons-material/Menu";
import LogoutIcon      from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LockResetIcon   from "@mui/icons-material/LockReset";
import { DRAWER_WIDTH } from "./Sidebar";
import { useAuth } from "../components/AuthContext";
import api from "../api/api";

const EMPTY_PASS = { current: "", nueva: "", confirmar: "" };

export default function Navbar({ onMenu }) {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user, logout } = useAuth();

  const [anchor, setAnchor] = useState(null);
  const [openPass, setOpenPass] = useState(false);
  const [passForm, setPassForm] = useState(EMPTY_PASS);
  const [passError, setPassError] = useState("");

  const username = user?.username || "User";
  const displayName = user?.nombre || username;
  const initials = displayName.split(" ").slice(0, 2).map(p => p[0]).join("").toUpperCase() || username.slice(0, 2).toUpperCase();

  const abrirCambiarPass = () => {
    setAnchor(null);
    setPassForm(EMPTY_PASS);
    setPassError("");
    setOpenPass(true);
  };

  const cambiarPassword = async () => {
    if (!passForm.current) return setPassError("Enter your current password.");
    if (passForm.nueva.length < 6) return setPassError("The new password must be at least 6 characters long.");
    if (passForm.nueva !== passForm.confirmar) return setPassError("Passwords do not match.");
    try {
      await api.post("/auth/change_password", {
        current_password: passForm.current,
        new_password: passForm.nueva,
      });
      setOpenPass(false);
    } catch (e) {
      setPassError(e.response?.data?.detail || "Error changing password.");
    }
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        ml: { md: `${DRAWER_WIDTH}px` },
        width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
        background: "#fff",
        borderBottom: "1px solid #e0e0e0",
        color: "text.primary",
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 2 } }}>
        {/* Hamburger button on mobile */}
        {isMobile && (
          <IconButton
            edge="start"
            onClick={onMenu}
            sx={{ mr: 1, color: "#C62828" }}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* App title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "#C62828", display: { xs: "none", sm: "block" } }}
          >
            Training Management System
          </Typography>
        </Box>

        {/* User + menu */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", display: { xs: "none", sm: "block" } }}
          >
            {displayName}
          </Typography>
          <IconButton
            onClick={(e) => setAnchor(e.currentTarget)}
            size="small"
            aria-label="User menu"
            aria-controls="user-menu"
            aria-haspopup="true"
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: "#C62828",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {initials}
            </Avatar>
          </IconButton>
        </Box>
      </Toolbar>

      {/* User dropdown menu */}
      <Menu
        id="user-menu"
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{ elevation: 3, sx: { mt: 0.5, minWidth: 180 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{displayName}</Typography>
          {user?.nombre && (
            <Typography variant="caption" color="text.secondary">@{username}</Typography>
          )}
          {!user?.nombre && (
            <Typography variant="caption" color="text.secondary">Active session</Typography>
          )}
        </Box>
        <Divider />
        <MenuItem onClick={abrirCambiarPass} sx={{ gap: 1.5, py: 1 }}>
          <LockResetIcon fontSize="small" />
          <Typography variant="body2">Change password</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => { setAnchor(null); logout(); }}
          sx={{ gap: 1.5, color: "error.main", py: 1 }}
        >
          <LogoutIcon fontSize="small" />
          <Typography variant="body2">Log out</Typography>
        </MenuItem>
      </Menu>

      {/* ── Change password dialog ── */}
      <Dialog open={openPass} onClose={() => setOpenPass(false)} fullWidth maxWidth="xs">
        <DialogTitle>Change password</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Current password"
            type="password"
            fullWidth
            value={passForm.current}
            onChange={e => { setPassForm({ ...passForm, current: e.target.value }); setPassError(""); }}
            autoComplete="current-password"
          />
          <TextField
            label="New password"
            type="password"
            fullWidth
            value={passForm.nueva}
            onChange={e => { setPassForm({ ...passForm, nueva: e.target.value }); setPassError(""); }}
            autoComplete="new-password"
            helperText="Minimum 6 characters"
          />
          <TextField
            label="Confirm new password"
            type="password"
            fullWidth
            value={passForm.confirmar}
            onChange={e => { setPassForm({ ...passForm, confirmar: e.target.value }); setPassError(""); }}
            autoComplete="new-password"
          />
          {passError && (
            <Typography variant="caption" color="error">{passError}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPass(false)}>Cancel</Button>
          <Button variant="contained" onClick={cambiarPassword}>Save</Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
}
